<script setup>
/**
 * Follow an organization from its website — the Follow button block's
 * dialog, and the web door to the same account TrailsIQ Go signs
 * people into. It was the old public hub page's (HubFollowDialog), and
 * moved here when that page was removed.
 *
 * Signed out, it IS the app's sign-in: an emailed six-digit code, and a
 * name the first time (AccountSheet.vue has the same three steps and
 * the same endpoints, through lib/reportAccount.js). The follow rides
 * the request that confirms the email (`verifySignInCode`'s `follow`),
 * so somebody who pressed Follow and then confirmed is following,
 * whatever happens to the tab afterwards.
 *
 * A follow is kept on the ACCOUNT: a browser cannot be sent a push, so
 * the confirmation says where the alerts arrive — the app, signed in
 * with the same email, which takes the follow up on its own.
 *
 * Rendered inside the site's root rather than teleported, so the
 * site's theme (--accent, --on-accent, --r, and Trailhead's --th-*)
 * reaches it — and mounted only while open, because a teleport in the
 * server-rendered page fails hydration. Every control has an id under
 * #tiq-site-follow, which is what harness/followblock_verify.mjs finds
 * them by.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import {
    accountUser, accountFollows, requestSignInCode, verifySignInCode, followOrg, unfollowOrg,
} from '@/lib/reportAccount.js';

const props = defineProps({
    // { name, slug }
    org: { type: Object, required: true },
    // Where the app is: its page for this org, which on a phone with
    // the app opens there and without it offers the store.
    appUrl: { type: String, required: true },
    // 'email' when signed out, 'done' after a signed-in Follow,
    // 'following' when the account already follows.
    start: { type: String, default: 'email' },
});
const emit = defineEmits(['close', 'open-app']);

const stage = ref(props.start); // email | code | name | done | following
const email = ref('');
const code = ref('');
const name = ref('');
const busy = ref(false);
const error = ref('');

const orgName = computed(() => props.org?.name || 'this organization');
const slug = computed(() => props.org?.slug || '');

function fail(e) {
    error.value = e?.message || 'Something went wrong. Try again.';
}

async function sendCode() {
    if (busy.value) return;
    const value = email.value.trim();
    if (!/^\S+@\S+\.\S+$/.test(value)) {
        error.value = 'Enter the email address the code should go to.';
        return;
    }
    busy.value = true;
    error.value = '';
    try {
        await requestSignInCode(value);
        if (stage.value === 'email') stage.value = 'code';
        await focus('#tiq-site-follow-code');
    } catch (e) {
        fail(e);
    } finally {
        busy.value = false;
    }
}

async function confirm() {
    if (busy.value) return;
    const digits = code.value.replace(/\D/g, '');
    if (digits.length !== 6) {
        error.value = 'The code is six digits.';
        return;
    }
    if (stage.value === 'name' && name.value.trim() === '') {
        error.value = 'Add your name to create the account.';
        return;
    }
    busy.value = true;
    error.value = '';
    try {
        const res = await verifySignInCode(
            email.value.trim(),
            digits,
            stage.value === 'name' ? name.value.trim() : null,
            { follow: slug.value },
        );
        // The server only records a follow it can honor; say so rather
        // than claiming one that did not happen.
        if (Array.isArray(res.follows) && !res.follows.includes(slug.value)) {
            error.value = `You're signed in, but ${orgName.value} couldn't be followed.`;
            stage.value = 'following';
            return;
        }
        stage.value = 'done';
    } catch (e) {
        if (e.code === 'needs_name') {
            stage.value = 'name';
            await focus('#tiq-site-follow-name');
        } else {
            fail(e);
        }
    } finally {
        busy.value = false;
    }
}

async function unfollow() {
    if (busy.value) return;
    busy.value = true;
    error.value = '';
    try {
        await unfollowOrg(slug.value);
        emit('close');
    } catch (e) {
        fail(e);
    } finally {
        busy.value = false;
    }
}

async function followAgain() {
    if (busy.value) return;
    busy.value = true;
    error.value = '';
    try {
        await followOrg(slug.value);
        stage.value = 'done';
    } catch (e) {
        fail(e);
    } finally {
        busy.value = false;
    }
}

const isFollowing = computed(() => accountFollows.value.includes(slug.value));

async function focus(selector) {
    await nextTick();
    document.querySelector(selector)?.focus();
}

function onKey(e) {
    if (e.key === 'Escape') emit('close');
}
onMounted(() => {
    document.addEventListener('keydown', onKey);
    focus(stage.value === 'email' ? '#tiq-site-follow-email' : '#tiq-site-follow-close');
});
onBeforeUnmount(() => document.removeEventListener('keydown', onKey));
</script>

<template>
    <div class="sf-scrim" @click.self="emit('close')">
        <div id="tiq-site-follow" class="sf" role="dialog" aria-modal="true" aria-labelledby="tiq-site-follow-title">
            <button id="tiq-site-follow-close" type="button" class="sf-x" aria-label="Close" @click="emit('close')">×</button>

            <template v-if="stage === 'email' || stage === 'code' || stage === 'name'">
                <h2 id="tiq-site-follow-title" class="sf-title">Follow {{ orgName }}</h2>
                <p class="sf-lede">
                    Create a free TrailsIQ Go account, or sign in to yours. Enter your email and we'll send you a code.
                    There's no password.
                </p>

                <form v-if="stage === 'email'" class="sf-form" @submit.prevent="sendCode">
                    <label class="sf-label" for="tiq-site-follow-email">Email</label>
                    <input
                        id="tiq-site-follow-email"
                        v-model="email"
                        class="sf-input"
                        type="email"
                        inputmode="email"
                        autocomplete="email"
                        autocapitalize="off"
                        maxlength="200"
                        placeholder="you@example.com"
                    />
                    <button id="tiq-site-follow-send" type="submit" class="sf-btn" :disabled="busy">
                        {{ busy ? 'Sending…' : 'Email me a code' }}
                    </button>
                </form>

                <form v-else class="sf-form" @submit.prevent="confirm">
                    <p class="sf-note">
                        We sent a code to <strong>{{ email.trim() }}</strong>. It works for 15 minutes.
                    </p>
                    <label class="sf-label" for="tiq-site-follow-code">Code</label>
                    <input
                        id="tiq-site-follow-code"
                        v-model="code"
                        class="sf-input sf-input--code"
                        type="text"
                        inputmode="numeric"
                        autocomplete="one-time-code"
                        maxlength="12"
                        placeholder="123456"
                    />
                    <template v-if="stage === 'name'">
                        <label class="sf-label" for="tiq-site-follow-name">Your name</label>
                        <input
                            id="tiq-site-follow-name"
                            v-model="name"
                            class="sf-input"
                            type="text"
                            autocomplete="name"
                            maxlength="80"
                            placeholder="First and last name"
                        />
                        <p class="sf-note sf-note--soft">
                            This email is new to us, so this creates your account. Trail crews see this name on reports you send under it.
                        </p>
                    </template>
                    <button id="tiq-site-follow-confirm" type="submit" class="sf-btn" :disabled="busy">
                        {{ busy ? 'Checking…' : (stage === 'name' ? 'Create account and follow' : 'Confirm and follow') }}
                    </button>
                    <div class="sf-links">
                        <button type="button" @click="stage = 'email'; code = ''; error = ''">Use another email</button>
                        <button type="button" :disabled="busy" @click="sendCode">Send a new code</button>
                    </div>
                </form>
            </template>

            <template v-else-if="stage === 'done'">
                <h2 id="tiq-site-follow-title" class="sf-title">You're following {{ orgName }}</h2>
                <p class="sf-lede">
                    To get alerts when trails open or close, install TrailsIQ Go on your phone and sign in with
                    <strong>{{ accountUser?.email }}</strong>. {{ orgName }} will already be followed there.
                </p>
                <a id="tiq-site-follow-app" class="sf-btn" :href="appUrl" @click="emit('open-app', $event)">Get TrailsIQ Go</a>
                <button id="tiq-site-follow-done" type="button" class="sf-btn sf-btn--quiet" @click="emit('close')">Done</button>
            </template>

            <template v-else>
                <h2 id="tiq-site-follow-title" class="sf-title">
                    {{ isFollowing ? `You follow ${orgName}` : `Follow ${orgName}` }}
                </h2>
                <p class="sf-lede">
                    Signed in as <strong>{{ accountUser?.email }}</strong>. Alerts arrive in TrailsIQ Go on your phone,
                    signed in with the same email.
                </p>
                <button v-if="isFollowing" id="tiq-site-follow-unfollow" type="button" class="sf-btn sf-btn--quiet" :disabled="busy" @click="unfollow">
                    {{ busy ? 'Unfollowing…' : 'Unfollow' }}
                </button>
                <button v-else id="tiq-site-follow-again" type="button" class="sf-btn" :disabled="busy" @click="followAgain">
                    {{ busy ? 'Following…' : 'Follow' }}
                </button>
                <a class="sf-btn sf-btn--quiet" :href="appUrl" @click="emit('open-app', $event)">Get TrailsIQ Go</a>
            </template>

            <p v-if="error" id="tiq-site-follow-error" class="sf-error" role="alert">{{ error }}</p>
        </div>
    </div>
</template>

<style scoped>
/* The site's own tokens: --accent and --on-accent (the text that reads
   on it) and --r (the corner radius) are on every template's root;
   --th-* are Trailhead's, and fall back to the Classic look. */
.sf-scrim {
    position: fixed; inset: 0; z-index: 1000;
    background: rgba(17, 20, 17, .55);
    display: flex; align-items: center; justify-content: center;
    padding: 16px;
}
.sf {
    position: relative; width: 100%; max-width: 420px;
    max-height: calc(100vh - 32px); overflow-y: auto;
    background: #fff; color: var(--th-ink, #1a1a1a);
    border-radius: calc(var(--r, 8px) + 6px);
    padding: 26px 22px 22px; box-shadow: 0 24px 60px rgba(0, 0, 0, .25);
    display: flex; flex-direction: column; gap: 12px;
    font-family: inherit; text-align: left;
}
.sf-x {
    position: absolute; top: 10px; right: 12px; width: 36px; height: 36px;
    border: 0; border-radius: var(--r, 8px); font-size: 24px; line-height: 1;
    color: var(--th-meta, #5c6159); background: transparent; cursor: pointer;
}
.sf-x:hover { background: var(--th-line-soft, #f1efe9); }
.sf-title {
    margin: 0; padding-right: 34px;
    font-family: var(--th-display, inherit);
    font-size: 22px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.15;
    color: inherit; text-transform: none;
}
.sf-lede { margin: 0; font-size: 15px; line-height: 1.45; color: var(--th-muted, #4b5563); }
.sf-lede strong, .sf-note strong { color: var(--th-ink, #1a1a1a); word-break: break-all; }
.sf-form { display: flex; flex-direction: column; gap: 8px; margin-top: 4px; }
.sf-label {
    font-size: 11px; font-weight: 700; letter-spacing: .09em;
    text-transform: uppercase; color: var(--th-meta, #5c6159); margin-top: 6px;
}
.sf-input {
    width: 100%; border: 1px solid var(--th-field, #d8d5cc); border-radius: var(--r, 8px);
    padding: 12px 14px; font: inherit; font-size: 16px; color: var(--th-ink, #1a1a1a); background: #fff;
}
.sf-input:focus { outline: 2px solid var(--accent, #dd9b36); outline-offset: 1px; border-color: transparent; }
.sf-input--code { letter-spacing: .3em; }
.sf-note { margin: 0; font-size: 14.5px; line-height: 1.45; color: var(--th-ink, #1a1a1a); }
.sf-note--soft { color: var(--th-meta, #5c6159); font-size: 13.5px; }
.sf-btn {
    display: flex; align-items: center; justify-content: center; width: 100%;
    margin-top: 6px; border: 0; border-radius: var(--r, 8px); padding: 13px 16px;
    font: inherit; font-size: 15px; font-weight: 700; text-align: center; text-decoration: none;
    background: var(--accent, #dd9b36); color: var(--on-accent, #fff); cursor: pointer;
}
.sf-btn:hover { filter: brightness(.94); text-decoration: none; color: var(--on-accent, #fff); }
.sf-btn:disabled { opacity: .6; cursor: default; }
.sf-btn--quiet, .sf-btn--quiet:hover {
    background: var(--th-line-soft, #f1efe9); color: var(--th-ink, #1a1a1a); margin-top: 0;
}
.sf-links { display: flex; justify-content: space-between; gap: 12px; margin-top: 4px; font-size: 14px; font-weight: 600; }
.sf-links button { border: 0; padding: 0; text-decoration: underline; background: none; color: var(--th-meta, #5c6159); cursor: pointer; font: inherit; }
.sf-error { margin: 0; font-size: 14px; color: #8e2f1a; background: #fbe4de; border-radius: var(--r, 8px); padding: 10px 12px; }
</style>
